import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { blend } from "@mui/system";
import { ALPHA, LOCATION_EXPANDED, LOCATION_GRID, NEUTRAL, SURFACE } from "@/theme/tokens";
import { deepLinkWashReducedMotion, deepLinkWashReplayStyle, deepLinkWashStyle } from "@/ui/Animations/deepLinkWash";
import { DETAIL_ROW_CLASS } from "@/ui/Tables/SystemDataGrid/utils";
import { ADD_SHIFT_ROW_CLASS, DRAFT_ROW_CLASS, SHIFT_ROW_CLASS } from "../rows/rowClasses";
import type { RowSpotlight } from "./useRowSpotlight";

/** What the wash needs from the spotlight: which rows, and how to paint them. */
export type SpotlightPaint = Pick<RowSpotlight, "spotlightIds" | "spotlightMode" | "flashGeneration">;

/** One row's cells, by the row's `data-id` (grid row ids are the rows' UUIDs, or a draft's `draft:{key}`). */
const rowCellsSelector = (id: string): string => `& .MuiDataGrid-row[data-id=${JSON.stringify(id)}] .MuiDataGrid-cell`;

/** One selector for every lit row's cells. */
const litCellsSelector = (ids: ReadonlySet<string>): string => Array.from(ids, rowCellsSelector).join(", ");

/**
 * The open row's job colour for its rail, set on the paint wrapper by `OpenRowRailColor` so a change of job repaints
 * the rail and nothing else. Unset, the rail is the brand colour.
 */
export const RAIL_COLOR_VAR = "--locations-open-rail-color";

/**
 * The rail on a cell's leading edge: an inset shadow, so it takes no layout and never shifts the content. Its colour
 * is the open row's job (`RAIL_COLOR_VAR`), or the brand's. The app sets the reading direction on the document (`dir`),
 * not on the theme, so a right-to-left page mirrors it by `:dir(rtl)`.
 */
const railSx = (theme: Theme): SystemStyleObject<Theme> => {
	const color = `var(${RAIL_COLOR_VAR}, ${theme.palette.primary.main})`;
	return {
		boxShadow: `inset ${LOCATION_EXPANDED.RAIL_WIDTH}px 0 0 ${color}`,
		"&:dir(rtl)": { boxShadow: `inset -${LOCATION_EXPANDED.RAIL_WIDTH}px 0 0 ${color}` },
	};
};

/**
 * The line kinds' own paint, the same on every render: a draft washed in the brand's lightest tint; the "Add shift"
 * line on plain paper closing its band; and the open editor (the grid's detail row) on that same light tint with the
 * rail down its leading edge. One row is open at a time (Q1), so every detail row is the open one or, for the
 * length of its exit, the one closing.
 */
const kindsSx = (theme: Theme): SystemStyleObject<Theme> => {
	const lightTint = alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY);
	return {
		[`& .${DRAFT_ROW_CLASS} .MuiDataGrid-cell`]: { backgroundColor: lightTint },
		[`& .${ADD_SHIFT_ROW_CLASS} .MuiDataGrid-cell`]: { backgroundColor: SURFACE, borderBottom: `1px solid ${NEUTRAL[200]}` },
		[`& .${ADD_SHIFT_ROW_CLASS}:hover, & .${DETAIL_ROW_CLASS}:hover`]: { backgroundColor: "transparent" },
		[`& .${DETAIL_ROW_CLASS} .MuiDataGrid-cell`]: {
			// Clipped, not hidden (MUI's cell default): a hidden overflow makes the cell a scroll box of its own, which the
			// editor's sticky action bar would stick inside instead of the grid's scroller. Clip cuts the same, scrolls nothing.
			overflow: "clip",
			// Top-aligned, not MUI's centred: the row learns the editor's new height a frame late, and centred content that
			// shrank first (e.g. after Fix the ladder) would drop by half the difference, then jump back.
			alignItems: "flex-start",
			backgroundColor: lightTint,
			borderBottom: `1px solid ${NEUTRAL[200]}`,
			...railSx(theme),
		},
	};
};

/**
 * The open row's paint: its cells a step deeper than the editor under it, and the same rail on its first cell,
 * so the row and its editor read as one thing. A rule on the open id, not a row class: `getRowClassName` stays at
 * module scope (a new one makes the grid re-read every row's height).
 */
const openRowSx = (theme: Theme, expandedRowId: string | null): SystemStyleObject<Theme> => {
	if (expandedRowId === null) return {};
	const cells = rowCellsSelector(expandedRowId);
	return {
		[cells]: { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) },
		// The first column's cell by the grid's own column index: `:first-of-type` never matches, because a row's first
		// child is the grid's left offset spacer, not a cell.
		[`${cells}[data-colindex="0"]`]: railSx(theme),
	};
};

/**
 * Hover, as the design's grid answers it: a shift or draft row washes in the brand's tint (its cells, so the open
 * row's own tint and a hovered row's are one colour, not stacked), and a location band one step lighter, over the
 * paper. The grid's own grey row hover is cleared under them. The band is found by its content's mark (the grid
 * draws its band rows itself, without a caller's row class), and its wash is opaque: the band cell paints its own
 * neutral ground, which a translucent wash would mix with. Under a pointer only, so a tap doesn't leave a row lit.
 */
const hoverSx = (theme: Theme): SystemStyleObject<Theme> => {
	const rowWash = alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY);
	const bandWash = blend(theme.palette.background.paper, theme.palette.primary.main, ALPHA.HOVER_OVERLAY);
	return {
		"@media (hover: hover) and (pointer: fine)": {
			[`&& :is(.${SHIFT_ROW_CLASS}, .${DRAFT_ROW_CLASS}):hover`]: { backgroundColor: "transparent" },
			[`&& :is(.${SHIFT_ROW_CLASS}, .${DRAFT_ROW_CLASS}):hover .MuiDataGrid-cell`]: { backgroundColor: rowWash },
			"&& .MuiDataGrid-row:has([data-locations-band]):hover": { backgroundColor: "transparent" },
			"&& .MuiDataGrid-row:has([data-locations-band]):hover .MuiDataGrid-cell": { backgroundColor: bandWash },
		},
	};
};

/** The column headers' type: a caption, so the headers label the columns without competing with the rows. */
const HEADER_SX = { "& .MuiDataGrid-columnHeader": { fontSize: LOCATION_GRID.HEADER_FONT_SIZE } } as const;

/**
 * The paint wrapper takes the grid's place in the page's flex row and lays the grid out as before. It can't be
 * `display: contents`: the grid sizes itself from its parent's box, and with none it reserves a scrollbar gutter.
 */
const GRID_WRAPPER_SX = { flex: 1, minWidth: 0, minHeight: 0, display: "flex" } as const;

/**
 * The Locations grid's own `sx`: the line kinds' tints, the hover washes and the header type. It depends only on the
 * theme, so it never changes identity.
 */
export const locationsGridSx = (theme: Theme): SystemStyleObject<Theme> => ({ ...kindsSx(theme), ...hoverSx(theme), ...HEADER_SX });

/**
 * What changes as the user works: the open row, then the spotlight wash, in that order, so a flash shows over an open
 * row. Painted from a wrapper around the grid, never through the grid's own `sx`: every prop
 * of MUI's grid reaches every row and cell through one shared context, so a new `sx` re-renders every visible cell,
 * while a new rule on the wrapper only repaints the cells it names.
 *
 * Painted on CELLS, never the row, because grid rows are absolutely positioned in the virtual scroller. The flash
 * is `deepLinkWashStyle`, or its replay twin on every other flash so a row flashed twice in a row washes again
 * (animated, reduced-motion safe); a hold is the same tint held still.
 */
export const locationsGridPaintSx = (
	theme: Theme,
	{ spotlightIds, spotlightMode, flashGeneration }: SpotlightPaint,
	expandedRowId: string | null,
): SystemStyleObject<Theme> => {
	const base = { ...GRID_WRAPPER_SX, ...openRowSx(theme, expandedRowId) };
	if (spotlightMode === null || spotlightIds.size === 0) return base;
	const wash =
		spotlightMode === "hold" ? deepLinkWashReducedMotion(theme) : flashGeneration % 2 === 0 ? deepLinkWashStyle(theme) : deepLinkWashReplayStyle(theme);
	return { ...base, [litCellsSelector(spotlightIds)]: wash };
};
